import { useRouter } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { FollowButton } from '@/components/ui/FollowButton';
import { Screen } from '@/components/ui/Screen';
import { Segmented } from '@/components/ui/Segmented';
import { useFavorites, useFollowedPlayers } from '@/features/favorites/hooks';
import { useAllTeams, useCompetitions } from '@/features/leagues/hooks';
import type { FavoriteEntityType } from '@/lib/supabase/types';
import { sortByPopularity } from '@/lib/utils/popularTeams';

type FollowingTab = 'teams' | 'competitions' | 'players';

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <View className="mb-5">
      <AppText
        variant="micro"
        weight="bold"
        className="mb-2 px-4 uppercase tracking-widest text-muted"
      >
        {label}
      </AppText>
      <Card className="mx-4 overflow-hidden">{children}</Card>
    </View>
  );
}

function EntityRow({
  crestUri,
  name,
  subtitle,
  entityType,
  entityId,
  onPress,
  first,
}: {
  crestUri: string | null | undefined;
  name: string;
  subtitle?: string | null;
  entityType: FavoriteEntityType;
  entityId: string;
  onPress: () => void;
  first: boolean;
}) {
  return (
    <View className={`flex-row items-center gap-3 px-4 py-3.5 ${first ? '' : 'border-t border-line'}`}>
      <Pressable
        accessibilityRole="button"
        onPress={onPress}
        className="flex-1 flex-row items-center gap-3 active:opacity-70"
      >
        <TeamCrest uri={crestUri} name={name} size="md" />
        <View className="flex-1">
          <AppText variant="caption" weight="semibold" className="text-ink" numberOfLines={1}>
            {name}
          </AppText>
          {subtitle ? (
            <AppText variant="micro" numberOfLines={1}>
              {subtitle}
            </AppText>
          ) : null}
        </View>
      </Pressable>
      <FollowButton entityType={entityType} entityId={entityId} />
    </View>
  );
}

/**
 * Following hub: manage your followed teams / competitions / players, with a
 * "suggested" list of big clubs to discover. Reached from the home header star.
 */
export default function FollowingScreen() {
  const router = useRouter();
  const { t } = useTranslation();
  const [tab, setTab] = useState<FollowingTab>('teams');

  const favorites = useFavorites();
  const teamsQuery = useAllTeams();
  const competitionsQuery = useCompetitions();
  const playersQuery = useFollowedPlayers();

  const followedTeamIds = useMemo(
    () =>
      new Set(
        (favorites.data ?? []).filter((f) => f.entity_type === 'team').map((f) => f.entity_id),
      ),
    [favorites.data],
  );
  const followedCompIds = useMemo(
    () =>
      new Set(
        (favorites.data ?? [])
          .filter((f) => f.entity_type === 'competition')
          .map((f) => f.entity_id),
      ),
    [favorites.data],
  );

  const allTeams = teamsQuery.data ?? [];
  const myTeams = allTeams.filter((team) => followedTeamIds.has(team.id));
  const suggestedTeams = sortByPopularity(
    allTeams.filter((team) => !followedTeamIds.has(team.id)),
  ).slice(0, 8);

  const allComps = competitionsQuery.data ?? [];
  const myComps = allComps.filter((c) => followedCompIds.has(c.id));
  const otherComps = allComps.filter((c) => !followedCompIds.has(c.id));
  const players = playersQuery.data ?? [];

  const tabs: { value: FollowingTab; label: string }[] = [
    { value: 'teams', label: t('following.teams') },
    { value: 'competitions', label: t('following.competitions') },
    { value: 'players', label: t('following.players') },
  ];

  return (
    <Screen>
      <BackHeader title={t('following.title')} />
      <View className="mb-3">
        <Segmented options={tabs} value={tab} onChange={setTab} />
      </View>
      <ScrollView contentContainerStyle={{ paddingBottom: 32 }}>
        {tab === 'teams' ? (
          <>
            {myTeams.length > 0 ? (
              <Section label={t('following.yourTeams')}>
                {myTeams.map((team, index) => (
                  <EntityRow
                    key={team.id}
                    crestUri={team.logo_url}
                    name={team.name}
                    subtitle={team.country}
                    entityType="team"
                    entityId={team.id}
                    onPress={() => router.push(`/team/${team.id}`)}
                    first={index === 0}
                  />
                ))}
              </Section>
            ) : null}
            <Section label={t('following.suggested')}>
              {suggestedTeams.map((team, index) => (
                <EntityRow
                  key={team.id}
                  crestUri={team.logo_url}
                  name={team.name}
                  subtitle={team.country}
                  entityType="team"
                  entityId={team.id}
                  onPress={() => router.push(`/team/${team.id}`)}
                  first={index === 0}
                />
              ))}
            </Section>
          </>
        ) : tab === 'competitions' ? (
          <>
            {myComps.length > 0 ? (
              <Section label={t('following.yourCompetitions')}>
                {myComps.map((competition, index) => (
                  <EntityRow
                    key={competition.id}
                    crestUri={competition.logo_url}
                    name={competition.name}
                    subtitle={competition.country}
                    entityType="competition"
                    entityId={competition.id}
                    onPress={() => router.push(`/competition/${competition.id}`)}
                    first={index === 0}
                  />
                ))}
              </Section>
            ) : null}
            {otherComps.length > 0 ? (
              <Section label={t('following.allCompetitions')}>
                {otherComps.map((competition, index) => (
                  <EntityRow
                    key={competition.id}
                    crestUri={competition.logo_url}
                    name={competition.name}
                    subtitle={competition.country}
                    entityType="competition"
                    entityId={competition.id}
                    onPress={() => router.push(`/competition/${competition.id}`)}
                    first={index === 0}
                  />
                ))}
              </Section>
            ) : null}
          </>
        ) : players.length > 0 ? (
          <Section label={t('following.yourPlayers')}>
            {players.map((player, index) => (
              <EntityRow
                key={player.id}
                crestUri={player.photo_url}
                name={player.known_as ?? player.name}
                subtitle={player.team?.name}
                entityType="player"
                entityId={player.id}
                onPress={() => router.push(`/player/${player.id}`)}
                first={index === 0}
              />
            ))}
          </Section>
        ) : (
          <EmptyState
            icon="person-outline"
            title={t('following.noPlayersTitle')}
            body={t('following.noPlayersBody')}
          />
        )}
      </ScrollView>
    </Screen>
  );
}
